import React from 'react';
import PropTypes from 'prop-types';

import { Section, Container, Group } from 'components/core/Page';
import { H2 } from 'components/core/Typography';
import { Text } from 'components/core/Text';

import { ClickToShowNumberButton } from 'components/contact/ClickToShowNumberButton';

import { SearchBox } from 'components/search/SearchBox';
import { SearchContentVariation } from 'components/search/SearchContent';
import { SearchBreadcrumbs } from 'components/search/SearchBreadcrumbs';

import AverageUnitPriceHeader from '../AverageUnitPrice/AverageUnitPriceHeader';
import AverageUnitPriceChart from '../AverageUnitPrice/AverageUnitPriceChart';

import { BestPricedUnits } from '../SearchContent/BestPricedUnits';

import { formatMonthDayYear } from '../AverageUnitPrice/utils';

import './seoContentVariation.scss';

export default function SEOContentVariation({
	phone,
	getTollfreePhoneNumber,
	searchParams,
	searchLocation,
	searchContent,
	faqContent,
	faqShowing,
	averageUnitPriceData,
	bestPricedUnits,
	canonicalUrl,
	cityUrl,
	stateUrl,
	isCLP,
	baseUrl,
	headline,
	amenity,
	primaryHeadline,
	nearMeKeyword,
	isNearMePage,
}) {
	const hasAveragePriceHistory = !!averageUnitPriceData?.priceHistory?.length;
	const hasPricingContent =
		hasAveragePriceHistory || !!bestPricedUnits?.length;

	// Last date in price history for refresh date
	const refreshDate =
		searchContent?.averageMonthlyUnitPrice?.priceHistory &&
		searchContent.averageMonthlyUnitPrice.priceHistory.length > 0
			? searchContent.averageMonthlyUnitPrice.priceHistory[
					searchContent.averageMonthlyUnitPrice.priceHistory.length -
						1
				]?.date
			: null;

	return (
		<div className="variation-bottom-content">
			{
				<div className="search-content-container">
					{!isNearMePage && hasPricingContent && (
						<Section className="average-price-data-section">
							<Group>
								{isCLP ? (
									<>
										<H2
											id="clp-pricing"
											className="sf-type-large"
											weight="bold"
										>
											{`Storage prices in ${searchLocation.city}, ${searchLocation.state}`}
										</H2>
										<p className="average-price-description">
											Prices vary by unit size and
											amenities. Compare facilities in{' '}
											{searchLocation.city} to find the
											right unit.
										</p>
									</>
								) : (
									<>
										<H2
											id="pricing"
											className="sf-type-large"
											weight="bold"
										>
											Pricing for{' '}
											{searchContent.formattedStorageType}{' '}
											in {searchLocation.city}?
										</H2>
										<p>
											Below are monthly price ranges you
											can expect to see in{' '}
											{searchLocation.city} based on
											different storage unit sizes.
										</p>
									</>
								)}
							</Group>

							{hasAveragePriceHistory && (
								<Group className="average-price-data-group">
									<AverageUnitPriceHeader
										averagePriceData={averageUnitPriceData}
										city={searchLocation?.city}
										state={searchLocation?.state}
									/>
									<AverageUnitPriceChart
										averagePriceData={averageUnitPriceData}
									/>
								</Group>
							)}

							{!!bestPricedUnits?.length && (
								<div className="best-priced-units-container">
									<BestPricedUnits
										location={{
											city: searchLocation?.city,
											state: searchLocation?.state,
										}}
										units={bestPricedUnits}
										refreshDate={
											refreshDate
												? formatMonthDayYear(
														refreshDate,
													)
												: undefined
										}
									/>
								</div>
							)}
						</Section>
					)}

					<SearchContentVariation
						location={searchLocation}
						content={searchContent}
						searchParams={searchParams}
						faqShowing={faqShowing}
						faqContent={faqContent}
						amenity={amenity}
						isNearMePage={isNearMePage}
					/>

					{searchParams.subPageType === 'cityLandingPage' &&
						phone && (
							<Section className="bottom-search-cta-section">
								<Container>
									<div className="search-bottom-cta-section">
										<Text
											size="large"
											family="rounded"
										>
											{headline}
										</Text>
										<div className="bottom-cta-content">
											<SearchBox
												inputLabel="Enter ZIP code"
												handleSearch={() => {}}
												buttonIcon
											/>

											<ClickToShowNumberButton
												className="phone-number-button"
												outline
												size="standard"
												fetchPhoneNumber={
													getTollfreePhoneNumber
												}
												label="Call Now"
											/>
										</div>
									</div>
								</Container>
							</Section>
						)}
				</div>
			}

			<Section className="search-breadcrumbs-section">
				<SearchBreadcrumbs
					location={searchLocation}
					canonicalUrl={canonicalUrl}
					cityUrl={isNearMePage ? '' : cityUrl}
					stateUrl={isNearMePage ? '' : stateUrl}
					searchParams={searchParams}
					baseUrl={baseUrl}
					isNewSearchPage={true}
					isCLPPage={true}
					primaryHeadline={primaryHeadline}
					nearMeKeyword={nearMeKeyword}
					isNearMePage={isNearMePage}
				/>
			</Section>
		</div>
	);
}

SEOContentVariation.propTypes = {
	phone: PropTypes.object,
	getTollfreePhoneNumber: PropTypes.func,
	searchParams: PropTypes.object,
	searchLocation: PropTypes.object,
	searchContent: PropTypes.object,
	faqContent: PropTypes.array,
	faqShowing: PropTypes.bool,
	averageUnitPriceData: PropTypes.object,
	bestPricedUnits: PropTypes.array,
	canonicalUrl: PropTypes.string,
	cityUrl: PropTypes.string,
	stateUrl: PropTypes.string,
	isCLP: PropTypes.bool,
	headline: PropTypes.string,
	baseUrl: PropTypes.string,
	amenity: PropTypes.string,
	isNearMePage: PropTypes.bool,
};

SEOContentVariation.defaultProps = {
	headline: "Can't decide? Let SpareFoot help you choose.",
};
